"use client";

import Link from "next/link";
import { cn } from "@/lib/utils";
import { EmptyState } from "@/components/ui/empty-state";
import { StatePanel } from "@/components/ui/state-panel";
import type { EndReason } from "@/lib/odds-screen/live/types";
import type { BlockingPill, BoardState, LeagueTab } from "./view-model";
import { minStakeText } from "./use-min-stake";

export const ENDED_COPY: Record<EndReason, { title: string; message: string }> = {
  event_started: { title: "This event has started", message: "Pregame odds for it are closed." },
  event_removed: { title: "This event is no longer posted", message: "Pick another event from the board." },
  topic_limit: { title: "This board is unavailable", message: "Too many boards are open at once." },
  unknown_topic: { title: "This board is unavailable", message: "This league or market is not on the live feed." },
  forbidden: { title: "This feed needs an active plan", message: "Your session no longer has access to it." },
  row_removed: { title: "This row is unavailable", message: "It is no longer on the live feed." },
  live_ev_unavailable: { title: "Live EV is unavailable", message: "This league's in-game prices are not on the live feed right now." },
};

function LeagueLinks({ leagues }: { leagues: readonly LeagueTab[] }) {
  if (leagues.length === 0) return null;
  return (
    <div className="flex flex-col items-center gap-2 pt-1">
      <span className="text-xs text-muted-foreground">Jump to a league with games:</span>
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        {leagues.map((league) => (
          <Link
            key={league.id}
            href={`/odds-screen/${league.id}`}
            className="flex items-center gap-1.5 rounded-md border border-surface-line bg-surface-base px-2.5 py-1 text-[13px] font-medium text-zinc-300 hover:bg-white/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <span>{league.label}</span>
            <span className="data-num text-[11px] text-zinc-500">{league.events}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function BoardStatePanel({ state, leagues = [] }: { state: BoardState; leagues?: readonly LeagueTab[] }) {
  switch (state.kind) {
    case "loading":
      return <StatePanel variant="loading" shape="page" title="Loading odds…" message="Connecting to the live feed." />;
    case "empty_league":
      return <EmptyState title="League unavailable" description="This league has no games posted on the live odds feed right now." secondary={<LeagueLinks leagues={leagues} />} />;
    case "empty_family":
      return <EmptyState title="Market unavailable" description="This market group is not available for this league." secondary={<LeagueLinks leagues={leagues} />} />;
    case "empty_board":
      return <EmptyState title="No events" description="No events are posted for this league and market right now." secondary={<LeagueLinks leagues={leagues} />} />;
    case "ended":
      return <StatePanel variant="warning" shape="page" title={ENDED_COPY[state.reason].title} message={ENDED_COPY[state.reason].message} />;
    default:
      return null;
  }
}

export function FilteredEmpty({ reason, minStake, onClear }: { reason: "day" | "college" | "best" | "minimum"; minStake: number; onClear: () => void }) {
  if (reason === "minimum") {
    return <EmptyState title="No best prices meet your minimum" description={`Your books have the best price on some events, but only with limits under ${minStakeText(minStake)}.`} action={{ label: "Turn off minimum", onClick: onClear }} />;
  }
  if (reason === "best") {
    return <EmptyState title="No best prices at your books" description="Every best price on this board is at a book you have turned off." action={{ label: "Show all events", onClick: onClear }} />;
  }
  if (reason === "college") {
    return <EmptyState title="No games match these filters" description="Nothing on this board fits the Top 25 or conference you picked." action={{ label: "Clear filters", onClick: onClear }} />;
  }
  return <EmptyState title="No games on this day" description="Pick another day above." action={{ label: "All days", onClick: onClear }} />;
}

const BLOCKING_COPY: Record<BlockingPill["kind"], string> = {
  limited: "Too many live tabs open. Close one to continue.",
  locked: "Your live access ended. Refresh to sign back in.",
};

const BLOCKING_MESSAGE: Record<BlockingPill["kind"], string> = {
  limited: "You have this open in too many tabs or on too many devices. Close one and this screen reconnects on its own.",
  locked: "Refresh the page to sign back in.",
};

export function ConnectionBanner({ pill }: { pill: BlockingPill }) {
  return <StatePanel variant="error" shape="banner" role="alert" title={BLOCKING_COPY[pill.kind]} />;
}

export function BlockingPanel({ pill }: { pill: BlockingPill }) {
  return <StatePanel variant="warning" shape="page" role="alert" title={BLOCKING_COPY[pill.kind]} message={BLOCKING_MESSAGE[pill.kind]} />;
}
